import { CSSProperties, ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { IDropdownOption } from '@/shared/Inputs/DropdownInput/dropdown.types.ts'
import { Primitive } from '@/types/global.ts'
import css from '../DropdownInput/dropdown.module.css'
import cn from '../../../utils/cn.ts'
import ArrowDown from '@/assets/icons/arrowDown.svg?react'
import usePopup from '../../hooks/usePopup.ts'
import dropdownUtils from '@/shared/Inputs/DropdownInput/dropdown.utils.ts'
import DropdownFadeInPanel from '@/shared/Dropdowns/DropdownFadeInPanel'
import TextInput from '../TextInput/index.tsx'
import { Virtuoso } from 'react-virtuoso'
import DropdownInputListItem from '@/shared/Dropdowns/DropdownInputListItem/index.tsx'

interface IProps<T extends Primitive> {
  options: IDropdownOption[]
  selectedOption: T
  name?: string
  title: string | ReactNode
  onSelect: (option: T, name: string) => void
  onOptionHover?: (option: T) => void
  disabled?: boolean
  menuStyle?: CSSProperties
  wrapperStyle?: CSSProperties
}

const SearchDropdownInput = <T extends Primitive>(props: IProps<T>) => {
  const ref = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const virtuosoRef = useRef(null)
  const { open, setOpen } = usePopup(ref)
  const [searchText, setSearchText] = useState('')

  const flattenOptions = useMemo(() => {
    return dropdownUtils.flattenOptions(props.options)
  }, [props.options])

  const { selectedOption, selectedOptionIndex } = useMemo(() => {
    const selectedOptionIndex = flattenOptions.findIndex((o) => o.value === props.selectedOption)
    if (selectedOptionIndex === -1) return { selectedOptionIndex, selectedOption: null }

    return { selectedOptionIndex, selectedOption: flattenOptions[selectedOptionIndex] }
  }, [props.selectedOption, flattenOptions])

  const filteredOptions = useMemo(() => {
    const allOptions = dropdownUtils.flattenOptions(props.options)
    if (!searchText.trim() || (props.selectedOption && searchText === selectedOption.title)) {
      return allOptions
    }

    return allOptions.filter((option) => (option.title as string).toLowerCase().includes(searchText.toLowerCase()))
  }, [props.options, searchText, open, props.selectedOption])

  useLayoutEffect(() => {
    if (open && selectedOptionIndex >= 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({ index: selectedOptionIndex, align: 'center' })
    }
  }, [open, selectedOptionIndex])

  const handleChange = (optionValue: T) => () => {
    if (props.disabled) return

    props.onSelect(optionValue, props.name)
    setOpen(false)
  }

  const handleClick = () => {
    if (props.disabled) return

    if (!open) {
      focusInput()
      setOpen(true)
      setSearchText(selectedOption ? selectedOption.title.toString() : '')
    } else {
      setOpen(false)
    }
  }

  const focusInput = () => {
    requestAnimationFrame(() => {
      inputRef.current?.focus()
    })
  }

  const renderOption = (option: IDropdownOption) => {
    return (
      <DropdownInputListItem
        key={option.value as any}
        onClick={handleChange(option.value)}
        active={option.value === selectedOption?.value}
        onMouseEnter={props.onOptionHover ? () => props.onOptionHover(option.value) : undefined}
      >
        <p className="text-nm">{option.title}</p>
      </DropdownInputListItem>
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative', ...props.wrapperStyle }}>
      <div
        className={css.wrapper}
        data-open={open}
        data-type="default"
        onClick={handleClick}
        style={{ padding: `${open ? '0' : ''}`, border: `${open ? 'none' : ''}` }}
      >
        {open && (
          <TextInput
            ref={inputRef}
            name={props.name}
            onChange={setSearchText}
            placeHolder="Начните вводить значение"
            value={searchText}
            onBlur={() => !searchText && setOpen(false)}
            inputStyle={{ padding: '0 9px', paddingRight: '30px' }}
          />
        )}
        {!open && (
          <div
            data-open={open}
            className={cn({
              'text-nm line-clamp': true,
              'text-placeholder': !Boolean(selectedOption),
            })}
            title={(selectedOption?.title ?? props.title).toString()}
            style={{ paddingRight: '15px' }}
          >
            {selectedOption?.title ?? props.title}
          </div>
        )}
        {!props.disabled && (
          <div style={{ position: 'absolute', top: '5px', right: '10px' }}>
            <ArrowDown
              data-open={open}
              data-selected={Boolean(selectedOption)}
              className={css.arrow}
              onClick={handleClick}
            />
          </div>
        )}
      </div>

      {props.options && (
        <DropdownFadeInPanel open={open} style={props.menuStyle}>
          <Virtuoso
            ref={virtuosoRef}
            style={{ height: '290px', scrollbarWidth: 'thin' }}
            totalCount={filteredOptions.length}
            itemContent={(index) => renderOption(filteredOptions[index])}
          />
        </DropdownFadeInPanel>
      )}
    </div>
  )
}

export default SearchDropdownInput
